// @ts-check
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
  phases,
  clinicalTrialStatuses,
  roles,
  sponsorsTypeahead,
  leadSponsorTypeahead,
} from 'services/inputTokens';
import { getFilter, getOrganisationView } from 'selectors/organisation';
import { setFilterGroup } from 'actions/organisation';
import { trialKeys } from 'components/Search/Filters/keys';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';

import OrgFilterField from './OrgFilterField';
import OrgFilterSection from './OrgFilterSection';
import { ORGANISATIONPEOPLEVIEW } from '../helpers';

// SCIL-17770: Clinical Trials section for the org People/Activity panels using
// the main-search sponsor-NAME semantics (leadSponsor + sponsors typeaheads).
// This is the shared `filters` slice — distinct from the org profile's Clinical
// Trials TAB, whose `clinicalTrialFilters.leadSponsor` is a boolean flag. Phase
// Start Date is only meaningful with a Phase selected (buildQuery drops
// phaseStartDate when `phase` is absent), so it is disabled without a phase —
// mirroring main search. `sponsorSuperOrgId` replaces main search's Min.
// Sponsored Trials slot: on a single-org page the useful question is "sponsored
// by THIS org", not a count threshold (the minSponsoredTrials trio is in
// orgPageExcludedFilters).

// Exactly the keys this section renders — the minSponsoredTrials trio from
// trialKeys is excluded on the org profile, so it is deliberately absent.
const activeKeys = [
  trialKeys.hasClinicalTrials.key,
  trialKeys.phase.key,
  trialKeys.phaseStartDate.key,
  trialKeys.overallStatus.key,
  trialKeys.leadSponsor.key,
  trialKeys.sponsors.key,
  trialKeys.roles.key,
  trialKeys.startDate.key,
  'sponsorSuperOrgId',
];

const OrgClinicalTrialsSection = ({ organisationId, loading }) => {
  // SCIL-17610: same sponsorSuperOrgId filter on both tabs, but the copy is
  // per-tab (product decision 30/07/2026): People asks about the people,
  // Activity about the trials.
  const isPeopleTab =
    useSelector(getOrganisationView) === ORGANISATIONPEOPLEVIEW;
  const sponsorCopy = isPeopleTab
    ? {
        labelText: 'Has Trials Sponsored by this Organisation',
        helpText:
          'People at this organisation personally listed on its sponsored trials.',
      }
    : {
        labelText: 'Trials Sponsored by this Organisation',
        helpText: 'Restricts results to clinical-trial activity.',
      };
  const phaseValue = useSelector(
    getFilter(organisationId, trialKeys.phase.key)
  );
  const phaseActive = phaseValue != null && phaseValue.length > 0;

  return (
    <OrgFilterSection
      organisationId={organisationId}
      icon='trial'
      title='Clinical Trials'
      activeKeys={activeKeys}
    >
      <OrgFilterField
        filterType='checkbox'
        filterId={trialKeys.hasClinicalTrials.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
        labelText='Listed on at least one clinical trial'
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Phase'
        options={phases}
        filterId={trialKeys.phase.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='datePicker'
        placeholder={['Phase Start From', 'Phase Start To']}
        type='range'
        filterId={trialKeys.phaseStartDate.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        horizontalTitle='Phase Start Date'
        disabled={!phaseActive || loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Status'
        options={clinicalTrialStatuses}
        filterId={trialKeys.overallStatus.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Lead Sponsor'
        filterId={trialKeys.leadSponsor.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={leadSponsorTypeahead}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='All Sponsors'
        filterId={trialKeys.sponsors.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={sponsorsTypeahead}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Role'
        options={roles}
        filterId={trialKeys.roles.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='datePicker'
        placeholder={['Trial Start From', 'Trial Start To']}
        type='range'
        filterId={trialKeys.startDate.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        checkValue={organisationId}
        filterId='sponsorSuperOrgId'
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
        helpText={sponsorCopy.helpText}
        labelText={sponsorCopy.labelText}
      />
    </OrgFilterSection>
  );
};

OrgClinicalTrialsSection.propTypes = {
  organisationId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

export default OrgClinicalTrialsSection;
